import { ButtonBase } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';

// Neutral white overlays; see the design note in theme/theme.tsx.
const QUIET_HOVER_BG = 'rgba(255, 255, 255, 0.04)';
const RAISED_HOVER_BG = 'rgba(255, 255, 255, 0.06)';
const RAISED_HOVER_BORDER = 'rgba(255, 255, 255, 0.12)';

/**
 * Ink on the filled primary button: white, per the design, not the theme's
 * dark primary.contrastText.
 */
const PRIMARY_BUTTON_FG = '#ffffff';

/** Shared chassis of the button variants; each variant overrides its deltas. */
function buttonBase(theme: Theme) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontFamily: 'inherit',
        fontSize: 12,
        borderRadius: 4,
        whiteSpace: 'nowrap',
        '&.Mui-disabled': {
            color: theme.palette.action.disabled,
        },
    } as const;
}

/** Borderless low-emphasis button (design `lk-quiet`). */
export const QuietButton = styled(ButtonBase)(({ theme }) => ({
    ...buttonBase(theme),
    padding: theme.spacing(0.5, 1),
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    transition: 'all 100ms',
    '&:hover': {
        backgroundColor: QUIET_HOVER_BG,
        color: theme.palette.text.primary,
    },
}));

/** Raised secondary button (design `lk-btn`). */
export const RaisedButton = styled(ButtonBase)(({ theme }) => ({
    ...buttonBase(theme),
    padding: theme.spacing(0.5, 1),
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.raised,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        backgroundColor: RAISED_HOVER_BG,
        borderColor: RAISED_HOVER_BORDER,
    },
}));

/** Filled primary button (design `lk-btn lk-btn-primary`). */
export const PrimaryButton = styled(ButtonBase)(({ theme }) => ({
    ...buttonBase(theme),
    padding: theme.spacing(0.5, 1),
    color: PRIMARY_BUTTON_FG,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        borderColor: theme.palette.primary.dark,
    },
    '&.Mui-disabled': {
        color: PRIMARY_BUTTON_FG,
        opacity: 0.55,
    },
}));
